import React from 'react';
import { Card, Grid, Typography, Box, IconButton, Chip } from '@mui/material';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import { getWebImageUrl } from '../../../../utils/image';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  Card: {
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    marginBottom: '12px',
    backgroundColor: '#fff',

    '.focused': {
      padding: '16px',
      borderRadius: '12px',
      boxShadow: '2px 2px 8px rgba(0,0,0,0.08)',
      borderLeft: `4px solid #3966f8`,
      marginBottom: '18px'
    }
  },
  CardContent: {
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    '.focused': {
      borderRadius: '8px',
      justifyContent: 'space-between'
    }
  }
}));

// Product type to icon mapping
const PRODUCT_TYPE_ICON_MAPPING = {
  medical: 'medical.png',
  'critical illness': 'critical.png',
  life: 'life.png',
  annuity: 'saving.png',
  saving: 'saving.png',
  'investment-linked': 'Pfund.png'
};

// Chance badge colors
const CHANCE_COLORS = {
  high: { bg: '#e8f5e9', text: '#2e7d32', label: 'gpt-product-recommendation-possibility-options-high' },
  medium: { bg: '#fff3e0', text: '#f57c00', label: 'gpt-product-recommendation-possibility-options-medium' },
  low: { bg: '#f5f5f5', text: '#757575', label: 'gpt-product-recommendation-possibility-options-low' }
};

const getProductIcon = productType => {
  const iconKey = PRODUCT_TYPE_ICON_MAPPING[productType.toLowerCase()] || 'other.png';
  const iconPath = `img/sub-categories/${iconKey}`;
  return getWebImageUrl(iconPath);
};

// displayMode: focused, default
const GPTProductCategoryCard = ({ product, intl, onCopyReason, onLazypackMatching, displayMode }) => {
  const { productName, productType, coverageAmount, coverageUnit, chance, reason } = product;
  const { classes } = useStyles();
  const chanceStyle = CHANCE_COLORS[chance] || CHANCE_COLORS.low;

  const handleCopyReason = () => {
    if (navigator.clipboard && reason) {
      navigator.clipboard.writeText(reason);
      onCopyReason?.();
    }
  };

  const captialFirstLetter = text => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  return (
    <Card className={`${classes.Card} ${displayMode === 'focused' ? classes.focused : ''}`}>
      <Grid container spacing={2}>
        {/* Header: Icon, Product Type, Chance Badge */}
        <Grid size={12} className={`${classes.CardContent} ${displayMode === 'focused' ? classes.focused : ''}`}>
          <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Grid>
              <Grid container spacing={1} sx={{ alignItems: 'center' }}>
                <Grid>
                  <Box
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={getProductIcon(productType)}
                      color="primary"
                      alt={productType}
                      style={{ width: 24, height: 24, objectFit: 'contain' }}
                    />
                  </Box>
                </Grid>
                <Grid>
                  <Typography style={{ color: '#333', fontSize: '20px' }}>{captialFirstLetter(productName)}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid>
              <Chip
                label={intl.formatMessage({ id: chanceStyle.label })}
                size="small"
                style={{
                  backgroundColor: chanceStyle.bg,
                  color: chanceStyle.text,
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Coverage Amount */}
        <Grid size={12}>
          <Box
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="body2" style={{ color: '#666', fontWeight: 700 }}>
              {intl.formatMessage({ id: 'gpt-product-recommendation-amount' })}
            </Typography>
            <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
              {coverageUnit} {coverageAmount}
            </Typography>
          </Box>
        </Grid>

        {/* Reason */}
        <Grid size={12}>
          <Grid container spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Grid size="grow">
              <Typography variant="body2" style={{ color: '#555', lineHeight: 1.6 }}>
                {reason}
              </Typography>
            </Grid>
            {onCopyReason && (
              <Grid>
                <IconButton size="small" onClick={handleCopyReason} title="Copy reason">
                  <FileCopyOutlined style={{ fontSize: 16, color: '#3966f8' }} />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Grid>
        {onLazypackMatching && (
          <Grid size={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="text" color="primary" onClick={() => onLazypackMatching(product)}>
              {intl.formatMessage({ id: 'gpt-product-recommendation-lazypack-matching-cta' })} &gt;
            </Button>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default GPTProductCategoryCard;
